import {
  FormEventHandler,
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
} from "react";

import Button from "components/Button";
import { ArrowLeftIcon, XCloseIcon } from "components/icons";

import cn from "lib/cn";

import styles from "./ModalRightDialog.module.css";

export interface ModalRightDialogProps {
  id?: string;
  open: boolean;
  openDetail?: boolean;
  detailContent?: ReactNode;
  className?: string;
  classNames?: {
    submitBtn?: string;
    childrenContainer?: string;
  };
  children?: React.ReactNode;
  disabledSubmit?: boolean;
  cancelLabel?: string;
  submitLabel?: string;
  submitHelperText?: string;
  title?: string;
  loading?: boolean;
  fullHeight?: boolean;
  action?: React.ReactNode;
  withBack?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  containerClassName?: string;
  childContainerClassName?: string;
  optionalButton?: React.ReactNode;
  defaultScrollbar?: boolean;
  actionClassName?: string;
  onClose?: () => void;
  onSubmit?: FormEventHandler<HTMLButtonElement>;
  onBack?: () => void;
}

const ModalRightDialog = forwardRef(
  ({ open, ...rest }: ModalRightDialogProps, _) => {
    if (!open) return null;

    return <ModalRightDialogMain open={open} {...rest} />;
  }
);

const ModalRightDialogMain = forwardRef(
  (
    {
      id,
      open,
      children,
      className,
      containerRef,
      submitLabel,
      submitHelperText,
      title,
      disabledSubmit,
      classNames,
      loading = false,
      fullHeight,
      action,
      withBack = false,
      openDetail,
      detailContent,
      containerClassName,
      childContainerClassName,
      optionalButton,
      defaultScrollbar = false,
      actionClassName,
      onBack,
      onClose,
      onSubmit,
    }: ModalRightDialogProps,
    _
  ) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const disabled = loading || disabledSubmit;

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== "Escape" || !onClose) return;
        // Avoid triger close right dialog if there is a modal
        const modals = document.querySelectorAll(".portal-elm");
        if (modals?.length) return;

        onClose();
      };

      document.body.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.removeEventListener("keydown", handleKeyDown);
      };
    }, [onClose]);

    return (
      <div id={id} data-testid={id}>
        <div
          className={cn(
            "h-screen z-[100] fixed bg-black/40 top-0 left-0 flex w-full duration-500",
            open ? "visible opacity-100" : "invisible opacity-0",
            containerClassName
          )}
        ></div>
        <div className="flex">
          {
            <div
              className={cn(
                "sm:mr-1 right-0 xl:max-w-[50%] transition-all sm:max-w-[30rem] w-full h-screen fixed sm:right-[50%] md:right-[40%] lg:right-[30%] xl:right-[25%] 2xl:right-[20%] z-[102] bg-white top-0",
                openDetail && open
                  ? "opacity-100 visible"
                  : "opacity-0 invisible",
                childContainerClassName
              )}
            >
              {detailContent}
            </div>
          }
          <div
            data-testid={id ? `content-${id}` : undefined}
            className={cn([
              "w-full sm:w-[50%] md:w-[40%] lg:w-[30%] xl:w-[25%] 2xl:w-[20%]  fixed h-screen bg-white flex flex-col right-0 top-0 bottom-0 duration-500 z-[101]",
              open ? "opacity-100 visible" : "opacity-0 hidden",
              className,
            ])}
          >
            <div className="h-[4rem] flex justify-between items-center  px-5  border-b-[0.125rem] border-line-500">
              <div className="flex items-center gap-4">
                {withBack && (
                  <button
                    data-testid={id ? `onBack-${id}` : undefined}
                    className={cn(
                      "w-[2.1rem] h-[2.1rem] flex justify-center items-center border-1 rounded border border-primaryBrand-500/20",
                      loading ? "cursor-not-allowed" : "cursor-pointer"
                    )}
                    onClick={loading ? undefined : onBack}
                  >
                    <ArrowLeftIcon
                      width={17}
                      height={17}
                      className="text-neutral-gray-200"
                    />
                  </button>
                )}

                {title && (
                  <p className="text-base text-night text-left font-medium">
                    {title}
                  </p>
                )}
              </div>
              {optionalButton}
              {!!onClose && (
                <button
                  ref={closeButtonRef}
                  className={cn(
                    "w-[2.1rem] h-[2.1rem] flex justify-center items-center border-1 rounded border border-gray-50",
                    loading ? "cursor-not-allowed" : "cursor-pointer"
                  )}
                  onClick={loading ? undefined : onClose}
                >
                  <XCloseIcon
                    width={13}
                    height={13}
                    data-testid={id ? `closeIcon-${id}` : undefined}
                  />
                </button>
              )}
            </div>

            <div
              className={cn(
                fullHeight
                  ? "h-[calc(100vh-6rem)] overflow-y-auto px-0 pt-3 pb-6"
                  : "h-[calc(100vh-10rem)] overflow-y-auto p-5 pt-3",
                fullHeight && submitLabel && "mb-[4.375rem]",
                classNames?.childrenContainer,
                defaultScrollbar ? undefined : styles.scrollBar
              )}
              ref={containerRef}
            >
              {children}
            </div>

            {submitLabel && (
              <div className="w-full flex flex-col gap-2 mb-2 p-5 bottom-0 absolute bg-white">
                {submitHelperText && (
                  <div className="text-center text-xs">{submitHelperText}</div>
                )}
                <div className="w-full flex justify-center">
                  <Button
                    onClick={disabled ? undefined : onSubmit}
                    className={cn(["min-w-[100%]", classNames?.submitBtn])}
                    disabled={disabled}
                    isLoading={loading}
                    color="primary"
                    size="md"
                  >
                    {submitLabel}
                  </Button>
                </div>
              </div>
            )}
            {action && (
              <div
                className={cn(
                  "w-full flex flex-col gap-2 p-5 mt-auto",
                  actionClassName
                )}
              >
                <div className="w-full flex justify-center">{action}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default ModalRightDialog;
